import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { Title } from '@angular/platform-browser';
import {MetaDefinition} from '@angular/platform-browser';
import { Router } from '@angular/router';


export interface PageMetadata {
  title: string;
  description: string;
  keywords: string[];
  type: string;
  ogTitle?: string;
  ogType?: string;
  ogUrl?: string;
  ogDescription?: string;
  ogImage?: string;
  canonicalLink?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

const defaultMetadata: PageMetadata = {
  title: 'App Title',
  description: 'App description',
  keywords: ['Angular', 'meta tags', 'Angular Universal'],
  type: 'website',
};
@Injectable({
  providedIn: 'root'
})

export class MetaService {

constructor(private meta: Meta, private titleService: Title,  @Inject(PLATFORM_ID) private platformId: any // Inject platform ID
) { }



  private metaMappings = {
    title: { name: 'title' },
    description: { name: 'description' },
    keywords: { name: 'keywords' },
    auteur: { name: 'author' },
    section: { property: 'article:section' },
    ogTitle: { property: 'og:title' },
    ogType: { property: 'og:type' },
    ogUrl: { property: 'og:url' },
    ogDescription: { property: 'og:description' },
    ogImage: { property: 'og:image' },
    publishedTime: { property: 'article:published_time' },
    modifiedTime: { property: 'article:modified_time' },
    twitterTitle: { name: 'twitter:title' },
    twitterDescription: { name: 'twitter:description' },
    twitterImage: { name: 'twitter:image' },
  };

  /**
   * Update meta tags dynamically. Clears unused tags.
   */
  updateMetaTags(metaData: {
    title?: string;
    description?: string;
    keywords?: string;
    auteur?: string;
    section?: string;
    ogTitle?: string;
    ogType?: string;
    ogUrl?: string;
    ogDescription?: string;
    ogImage?: string;
    publishedTime?: string;
    modifiedTime?: string;
    twitterTitle?: string;
    twitterDescription?: string;
    twitterImage?: string;
    canonicalLink?: string;
  }) {
    // Update or clear all meta tags
    Object.keys(this.metaMappings).forEach((key) => {
      const tagSelector = this.metaMappings[key];
      const content = metaData[key];
      if (content) {
        this.updateOrAddMetaTag(tagSelector, content);
      } else {
        this.clearMetaTag(tagSelector);
      }
    });

    // Handle page title
    if (metaData.title) {
      this.titleService.setTitle(metaData.title);
    } else {
      this.titleService.setTitle('Default Title'); // Optional default
    }

    // Handle canonical link
    if (metaData.canonicalLink) {
      this.updateCanonicalLink(metaData.canonicalLink);
    } else {
      this.clearCanonicalLink();
    }
  }

  /**
   * Add or update a specific meta tag
   */
  private updateOrAddMetaTag(selector: { name?: string; property?: string }, content: string) {
    const attribute = selector.name ? 'name' : 'property';
    const value = selector.name || selector.property;
    const existingTag = this.meta.getTag(`${attribute}="${value}"`);

    if (existingTag) {
      this.meta.updateTag({ ...selector, content });
    } else {
      this.meta.addTag({ ...selector, content });
    }
  }

  /**
   * Remove a specific meta tag
   */
  private clearMetaTag(selector: { name?: string; property?: string }) {
    const attribute = selector.name ? 'name' : 'property';
    const value = selector.name || selector.property;

    const existingTag = this.meta.getTag(`${attribute}="${value}"`);
    if (existingTag) {
      this.meta.removeTag(`${attribute}="${value}"`);
    }
  }

  /**
   * Update the canonical link dynamically
   */
  private updateCanonicalLink(href: string) {
    if (isPlatformBrowser(this.platformId)) {
      let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    }
  }

  /**
   * Remove the canonical link
   */
  private clearCanonicalLink() {
    if (isPlatformBrowser(this.platformId)) {
      const existingLink = document.querySelector('link[rel="canonical"]');
      if (existingLink) {
        existingLink.remove();
      }
    }
  }
  updateFinancialMetaTags(financialData: {
    stockSymbol?: string;
    stockExchange?: string;
    stockSector?: string;
    stockCurrency?: string;
    stockIsin?: string;
    stockLastUpdated?: string;
  }) {
    const financialMappings = {
      stockSymbol: { name: 'stock:symbol' },
      stockExchange: { name: 'stock:exchange' },
      stockSector: { name: 'stock:sector' },
      stockCurrency: { name: 'stock:currency' },
      stockIsin: { name: 'stock:isin' },
      stockLastUpdated: { name: 'stock:lastUpdated' },
    };
  
    Object.keys(financialMappings).forEach((key) => {
      const tagSelector = financialMappings[key];
      const content = financialData[key];
      if (content) {
        this.updateOrAddMetaTag(tagSelector, content);
      } else {
        this.clearMetaTag(tagSelector);
      }
    });
  }
}